import { api } from './request';
import type { Node } from '@cutr/constants/order';
import type { SupplierMaterialGroup } from '@cutr/constants/material';
import type {
  PartsCreateData,
  PartsFromContoursData,
} from '@cutr/constants/part';

/**
 * `Node` minus the relations the payload leaves unpopulated.
 */
export type PartNode = Omit<Node, 'supplierMaterialGroup' | 'materialSwatch'>;

export type PartUpdateData = {
  partIds: string[];
  topMaterialId?: string | null;
  coreMaterialId?: string | null;
  bottomMaterialId?: string | null;
  properties?: Node['properties'];
};

type QueryParams = {
  latestUpdatedAt: number | null;
};

export type ResponseData = {
  materialGroups: SupplierMaterialGroup[];
  nodes: PartNode[];
  latestUpdatedAt: number;
};

export type PartDisplayNameData = {
  partIds: string[];
  displayName: string;
};

export type PartDoNotProduceData = {
  partIds: string[];
  doNotProduce: boolean;
};

/**
 * Frozen nodes are never deep-proxied, so the cached array costs one proxy instead
 * of thousands, and a write to a node throws instead of silently doing nothing.
 */
function freezeNodes(data: ResponseData) {
  data.nodes.forEach((node) => Object.freeze(node));
  return data;
}

export async function getParts(variantId: string, signal?: AbortSignal) {
  const { data } = await api.get<ResponseData>(
    `/v2/order-variants/${variantId}/parts`,
    { signal }
  );

  return freezeNodes(data);
}

export function putPartData(
  variantId: string,
  params: QueryParams,
  data: PartUpdateData
) {
  return api
    .put<ResponseData>(`/v2/order-variants/${variantId}/parts`, data, {
      params,
    })
    .then(({ data }) => freezeNodes(data));
}

export async function putPartDisplayName(
  variantId: string,
  params: QueryParams,
  data: PartDisplayNameData
) {
  return api
    .put<ResponseData>(
      `/v2/order-variants/${variantId}/parts/display-name`,
      data,
      {
        params,
      }
    )
    .then(({ data }) => freezeNodes(data));
}

export async function putDoNotProduce(
  variantId: string,
  params: QueryParams,
  data: PartDoNotProduceData
) {
  return api
    .put<ResponseData>(
      `/v2/order-variants/${variantId}/parts/donotproduce`,
      data,
      { params }
    )
    .then(({ data }) => freezeNodes(data));
}

export function createParts(variantId: string, data: PartsCreateData) {
  return api.post(`/v2/order-variants/${variantId}/parts`, data);
}

export function createPartsFromContours(
  variantId: string,
  data: PartsFromContoursData
) {
  return api.post(
    `/v2/order-variants/${variantId}/parts/create-from-part-contours`,
    data
  );
}
